/** Here is the typical component from react being rendered for the corresponding form*/
import React, { useEffect, useState } from "react";
import EntityForm from "../EntityForm/EntityForm";
import { entityToFormValuesMapper } from "../../Common/utils";
import { FullWidthLoader } from "../../../../components/full-width-loader";
import { useConfig } from "../../../../lib/config";
import axios from "http/customAxios";
import asBookingEngineEntity from "../Common/BookingEngineEntityHOC";
import CONSTANTS from "../constants";
import ModificationBookingWindowForm from "./ModificationBookingWindow/ModificationBookingWindowForm";
import ModificationBookingWindowList from "./ModificationBookingWindow/ModificationBookingWindowList";

class ModificationForm extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {};
  }

  async componentDidMount() {
    const entities = await this.props.fetchEntities();
    this.setState({ entities });
  }

  getFields = ({ fields, entity = {}, entities = [] }) => {
    if (!fields || !fields.form) {
      return undefined;
    }
    // we filter conditions that are currently assigned to other entities
    const unavailableConditions = entities
      .filter(it => it.id !== entity.id)
      .reduce((acc, it) => [...acc, ...it.conditionRates], []);
    const availableConditions = (this.props.conditions || []).filter(
      it => unavailableConditions.indexOf(it.ticker) === -1
    );
    return fields.form.map(it =>
      it.fieldName === "conditionRates"
        ? {
            ...it,
            options: availableConditions,
          }
        : it
    );
  };

  customEntityToFormValuesMapper = ({ entity, fields, locales, translationConfig }) => {
    const formValues = entityToFormValuesMapper({
      entity,
      fields,
      locales,
      translationConfig,
    });

    const datesMinRelease = entity.datesMinRelease
      ? entity.datesMinRelease.duration
      : formValues.datesMinRelease;
    const accommodationMinRelease = entity.accommodationMinRelease
      ? entity.accommodationMinRelease.duration
      : formValues.accommodationMinRelease;
    return {
      id: formValues.id,
      propertyTicker: formValues.propertyTicker || formValues.hotelTicker,
      ticker: formValues.ticker,
      canModifyDates: formValues.canModifyDates,
      canChangeConditions: formValues.canChangeConditions,
      datesMinRelease,
      datesModificationRestrictionType: formValues.datesModificationRestrictionType,
      canModifyAccommodations: formValues.canModifyAccommodations,
      accommodationMinRelease,
      accommodationModificationRestrictionType: formValues.accommodationModificationRestrictionType,
      conditionRates: formValues.conditionRates,
      maxAllowedChanges: formValues.maxAllowedChanges,
      modificationAllowedFrom: formValues.modificationAllowedFrom,
      modificationAllowedUntil: formValues.modificationAllowedUntil,
      excludedStayDates: formValues.excludedStayDates || [],
    };
  };

  //TODO: Chrono Unit is always DAYS!
  convertToEntity = formValues => ({
    propertyTicker: formValues.propertyTicker || formValues.hotelTicker,
    ticker: formValues.ticker,
    canModifyDates: formValues.canModifyDates,
    canChangeConditions: formValues.canChangeConditions,
    datesMinRelease: {
      duration: parseFloat(formValues.datesMinRelease),
      chronoUnit: "DAYS",
    },
    datesModificationRestrictionType: formValues.datesModificationRestrictionType,
    canModifyAccommodations: formValues.canModifyAccommodations,
    accommodationMinRelease: {
      duration: parseFloat(formValues.accommodationMinRelease),
      chronoUnit: "DAYS",
    },
    accommodationModificationRestrictionType: formValues.accommodationModificationRestrictionType,
    conditionRates:
      formValues.conditionRates && formValues.conditionRates.length > 0
        ? formValues.conditionRates
        : null,
    maxAllowedChanges: formValues.maxAllowedChanges ?? 0,
    modificationAllowedFrom: formValues.modificationAllowedFrom || "",
    modificationAllowedUntil: formValues.modificationAllowedUntil || "",
    excludedStayDates: formValues.excludedStayDates || [],
  });

  handleSubmit = ({ formValues }) => {
    //TODO: handle case when there's an original entity
    //TODO: this could be handled in a generic way.
    const { id, ...otherFormValues } = formValues;

    const entity = this.convertToEntity(otherFormValues);

    if (id !== "" && id !== " " && id !== null && typeof formValues.id !== "undefined") {
      entity.id = formValues.id;
    }

    this.props.onSubmitEntity({
      entity,
    });
  };

  /* Handle Errors */
  render() {
    const { name, validation, fields, renderers, translation, entities } = this.props.config;
    const {
      locales,
      entity,
      entityError,
      entityResult,
      entityName,
      loading,
      getListPath,
      locale,
    } = this.props;
    const { getFields, handleSubmit } = this;

    const nestedFormWidgets = {
      excludedStayDates: asBookingEngineEntity(ModificationBookingWindowForm, {
        entityName: CONSTANTS.ENTITY.MODIFICATIONBOOKINGWINDOW.name,
        type: "FORM",
      }),
    };

    const nestedListWidgets = {
      excludedStayDates: asBookingEngineEntity(ModificationBookingWindowList, {
        entityName: CONSTANTS.ENTITY.MODIFICATIONBOOKINGWINDOW.name,
        type: "LIST",
      }),
    };

    return (
      <div className={"wbkv9-form-modifications"}>
        <EntityForm
          loading={loading}
          name={name}
          entity={entity}
          entities={entities}
          validation={validation}
          renderers={renderers}
          fields={getFields({ fields, entities: this.state.entities, entity })}
          locales={locales}
          translation={translation}
          onSubmit={handleSubmit}
          entityError={entityError}
          entityResult={entityResult}
          entityName={entityName}
          getListPath={getListPath}
          entityToFormValuesMapper={this.customEntityToFormValuesMapper}
          locale={locale}
          nestedFormWidgets={nestedFormWidgets}
          nestedListWidgets={nestedListWidgets}
        />
      </div>
    );
  }
}

ModificationForm.propTypes = {};

ModificationForm.defaultProps = {
  locales: ["es", "ca", "en", "fr", "it", "de"],
};

function getConditionNameTranslated({ condition, locale, ticker }) {
  const translation =
    condition.translations.find(it => it.locale === locale) || condition.translations[0] || {};
  return translation.name || ticker;
}

function ModificationFormWrapper(props) {
  const { apiEndpoint } = useConfig();
  const [loading, setLoading] = useState(true);
  const [conditions, setConditions] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      // TODO: replace with httpclient
      const response = await axios.get(
        `${apiEndpoint}/api/backoffice/${props.hotelTicker}/conditions/`
      );
      const conditions =
        response.data.map(it => ({
          ticker: it.ticker,
          name: getConditionNameTranslated({
            condition: it,
            locale: props.locale,
            ticker: it.ticker,
          }),
        })) || [];
      setConditions(conditions);
      setLoading(false);
    };
    fetch();
  }, [apiEndpoint, props.hotelTicker, props.locale]);

  if (loading) {
    return <FullWidthLoader />;
  }
  return <ModificationForm {...props} conditions={conditions} />;
}

export default ModificationFormWrapper;
